import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Archive, Check, Pencil, RotateCcw, X } from "lucide-react";

export function PlaceTemplatesDialog({ onClose, onChanged }) {
  const [items, setItems] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/place-templates", { params: { include_archived: true } }).then(({ data }) => setItems(data.items));
  useEffect(() => { load(); }, []);
  const patch = async (t, body) => {
    try { await api.patch(`/place-templates/${t.id}`, body); setEdit(null); await load(); onChanged(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="place-templates-dialog" className="rounded-xl sm:max-w-lg max-h-kb overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Templates</DialogTitle></DialogHeader>
        <p className="text-[12px] text-slate-500">Made when you tick "Save as a template", or from what's already in the app. Changing or archiving one never changes anything already made.</p>
        <div className="divide-y divide-slate-100">
          {(items || []).map((t) => (
            <div key={t.id} data-testid={`place-template-${t.id}`} className={`flex items-center gap-2 py-2 ${t.archived ? "opacity-50" : ""}`}>
              {edit?.id === t.id ? (
                <>
                  <Input data-testid={`place-template-name-${t.id}`} value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} className="h-9 flex-1" />
                  <Button size="sm" data-testid={`place-template-save-${t.id}`} onClick={() => patch(t, { name: edit.name })} className="h-9"><Check className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => setEdit(null)} className="h-9"><X className="w-4 h-4" /></Button>
                </>
              ) : (
                <>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold">{t.name}{t.archived && " · archived"}</div>
                    <div className="text-[11px] text-slate-500">{t.summary}{t.starter ? " · from the live app" : ""}</div>
                  </div>
                  <Button size="sm" variant="ghost" data-testid={`place-template-rename-${t.id}`} onClick={() => setEdit({ id: t.id, name: t.name })}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" data-testid={`place-template-${t.archived ? "restore" : "archive"}-${t.id}`} onClick={() => patch(t, { archived: !t.archived })}>
                    {t.archived ? <RotateCcw className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                  </Button>
                </>
              )}
            </div>
          ))}
          {items && !items.length && <p className="py-4 text-sm text-slate-400">No templates yet.</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
